import { inject, Injectable, Signal } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PaymentDetails } from 'models/payment-details';
import { Person } from 'models/person';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { AccountHolderTypes } from 'shared/base/generics/enums/account-holders';
import { ModalService } from 'shared/base/generics/globals/modals/modal.service';
import { FormatAccountNumberPipe } from 'shared/base/generics/pipes/format-account-number.pipe';
import { FormInput } from 'sharedStores/cms/models/pages/form/form-input';
import { FormText } from 'sharedStores/cms/models/pages/form/form-text';
import { DialogComponent } from 'templates/dialogs/dialog/dialog.component';
import { nameSurnameValidator } from 'shared/base/generics/directives/name-surname-validator.directive';
import {
    addFormControls,
    removeFormControls,
} from 'shared/base/generics/helpers/form-value-operations';
import cloneDeep from 'lodash/cloneDeep';
import { Store } from '@ngrx/store';
import { PaymentSelectors } from 'stores/payment-details/payment.module';
import { AccountDetailsPageData } from 'models/page-data/account-details-page-data';
import { updateHouseholdMember } from 'stores/personal/personal.actions';
import { IdType } from 'enums/id-type';
import { PassportOrId } from 'models/passport-or-id';
import { QuoteControllerService_Token } from 'shared/base/generics/injection-tokens/dependency-tokens';
import { DropOffCode } from 'shared/base/generics/enums/drop-off-code';

@UntilDestroy()
@Injectable()
export class PaymentDetailsService {
    public dialogModal: NzModalRef;
    public paymentDetails = this.store$.selectSignal(PaymentSelectors.selectPaymentAVSDetails);
    public showPassportOrId = this.store$.selectSignal(
        PaymentSelectors.selectAvsShowPassport({
            personSequenceNumber: this.paymentDetails()?.selectedAccountHolder?.itemKey,
        }),
    );
    public quoteController = inject(QuoteControllerService_Token);

    constructor(
        public store$: Store,
        private readonly modal: ModalService,
    ) {}

    populateAccountDetails(
        houseHoldMember: Person,
        data: PaymentDetails,
        accountHolderType: string,
        dataForm: UntypedFormGroup,
        formatAccountNumber: FormatAccountNumberPipe,
    ) {
        switch (accountHolderType) {
            case AccountHolderTypes.POLICY_HOLDER:
                dataForm?.controls.accountNumber.setValue(
                    formatAccountNumber.transform(data.accountNumber),
                );
                break;
            case AccountHolderTypes.HOUSEHOLD_MEMBER:
            case AccountHolderTypes.ADDITIONAL_HOUSEHOLD_MEMBER:
                dataForm?.controls.accountNumber.setValue(
                    formatAccountNumber.transform(data.accountNumber),
                );
                dataForm?.controls.accountHolderCellNumber.setValue(houseHoldMember?.phone);
                dataForm.controls.accountHolderPermission.setValue(data.accountHolderPermission);
                if (dataForm.controls.idNumber) {
                    dataForm.controls.idNumber.setValue(houseHoldMember?.identification?.idNumber);
                }
                break;
            case AccountHolderTypes.ANOTHER_INDIVIDUAL:
                dataForm?.controls.accountHolderName.setValue(data.accountHolderName);
                dataForm?.controls.accountNumber.setValue(
                    formatAccountNumber.transform(data.accountNumber),
                );
                dataForm?.controls.idNumber.setValue(data.idBusinessRegistrationNumber);
                dataForm?.controls.accountHolderCellNumber.setValue(data.cellNumber);
                dataForm.controls.accountHolderPermission.setValue(data.accountHolderPermission);
                if (dataForm.controls.idNumber) {
                    dataForm.controls.idNumber.setValue(data.idBusinessRegistrationNumber);
                }
                break;
            case AccountHolderTypes.BUSINESS_OR_ORGANISATION:
                dataForm?.controls.accountHolderName.setValue(data.accountHolderName);
                dataForm?.controls.accountNumber.setValue(
                    formatAccountNumber.transform(data.accountNumber),
                );
                dataForm?.controls.businessRegistrationNumber.setValue(
                    formatAccountNumber.transform(data.idBusinessRegistrationNumber),
                );
                dataForm?.controls.businessRepresentativeName.setValue(data.surnameCompanyName);
                dataForm?.controls.accountHolderCellNumber.setValue(data.cellNumber);
                dataForm.controls.accountHolderPermission.setValue(data.accountHolderPermission);
                break;
        }
    }

    disableFields(
        avsLimitReached: Signal<boolean>,
        accountHolderCode: string,
        dataForm: UntypedFormGroup,
    ) {
        if (avsLimitReached()) {
            switch (accountHolderCode) {
                case AccountHolderTypes.POLICY_HOLDER:
                    dataForm.controls.accountNumber.disable();
                    if (dataForm.controls.idNumber) {
                        dataForm.controls.idNumber.disable();
                    }
                    break;

                case AccountHolderTypes.HOUSEHOLD_MEMBER:
                case AccountHolderTypes.ADDITIONAL_HOUSEHOLD_MEMBER:
                    dataForm.controls.accountNumber.disable();
                    dataForm.controls.accountHolderCellNumber.disable();
                    dataForm.controls.accountHolderPermission.disable();
                    if (dataForm.controls.idNumber) {
                        dataForm.controls.idNumber.disable();
                    }
                    break;

                case AccountHolderTypes.ANOTHER_INDIVIDUAL:
                    dataForm.controls.accountHolderName.disable();
                    dataForm.controls.accountNumber.disable();
                    dataForm.controls.idNumber.disable();
                    dataForm.controls.accountHolderCellNumber.disable();
                    dataForm.controls.accountHolderPermission.disable();
                    break;

                case AccountHolderTypes.BUSINESS_OR_ORGANISATION:
                    dataForm.controls.accountHolderName.disable();
                    dataForm.controls.accountNumber.disable();
                    dataForm.controls.businessRegistrationNumber.disable();
                    dataForm.controls.businessRepresentativeName.disable();
                    dataForm.controls.accountHolderCellNumber.disable();
                    dataForm.controls.accountHolderPermission.disable();
                    break;
            }
        }
    }

    updateHouseholdMember(householdMember: Person, dataForm: UntypedFormGroup, idType: IdType) {
        const person = cloneDeep(householdMember);

        if (person) {
            person.phone = dataForm.controls.accountHolderCellNumber.value;

            if (dataForm.controls.idNumber) {
                person.identification = {
                    ...person.identification,
                    idNumber: dataForm.controls.idNumber.value,
                    idType: idType,
                };
            }

            this.store$.dispatch(updateHouseholdMember({ person }));
        }
    }

    generateApiRequest(
        accountHolderCode: string,
        policyHolder: { fullName: string; person: Person },
        householdMember: { fullName: string; person: Person },
        dataForm: UntypedFormGroup,
        paymentDetails: PaymentDetails,
        isIdNumber: boolean,
    ): PaymentDetails {
        let request: PaymentDetails = cloneDeep(paymentDetails);
        switch (accountHolderCode) {
            case AccountHolderTypes.POLICY_HOLDER:
                request = this.setPersonRequestObject(dataForm, policyHolder, paymentDetails, true);
                break;
            case AccountHolderTypes.HOUSEHOLD_MEMBER:
            case AccountHolderTypes.ADDITIONAL_HOUSEHOLD_MEMBER:
                request = this.setPersonRequestObject(
                    dataForm,
                    householdMember,
                    paymentDetails,
                    true,
                );
                break;
            case AccountHolderTypes.ANOTHER_INDIVIDUAL:
                request = this.setAnotherIndividualRequestObject(
                    dataForm,
                    paymentDetails,
                    isIdNumber,
                );
                break;
            case AccountHolderTypes.BUSINESS_OR_ORGANISATION:
                request = this.setBusinessRequestObject(dataForm, paymentDetails);
                break;
        }
        return request;
    }

    setPersonRequestObject(
        dataForm: UntypedFormGroup,
        person: { fullName: string; person: Person },
        paymentDetails: PaymentDetails,
        needsPermission: boolean = false,
    ): PaymentDetails {
        const request: PaymentDetails = cloneDeep(paymentDetails);
        request.personSequenceNumber = person.person.sequenceNumber;
        request.accountHolderType = paymentDetails.accountHolderType;
        request.accountHolderName = person?.fullName;
        request.accountNumber = dataForm.controls.accountNumber.value.replace(/\s/g, '');
        request.surnameCompanyName = person?.person.lastName;
        request.idBusinessRegistrationNumber = person.person.identification.idNumber;
        request.cellNumber = person.person.phone;
        request.idType = person?.person?.identification?.idType;

        if (needsPermission) {
            request.accountHolderPermission =
                dataForm?.controls?.accountHolderPermission?.value ?? true;
        }

        return request;
    }

    setAnotherIndividualRequestObject(
        dataForm: UntypedFormGroup,
        paymentDetails: PaymentDetails,
        isIdNumber: boolean,
    ): PaymentDetails {
        const request: PaymentDetails = cloneDeep(paymentDetails);
        request.accountHolderType = paymentDetails.accountHolderType;
        request.accountHolderName = dataForm.controls.accountHolderName.value;
        request.accountNumber = dataForm.controls.accountNumber.value.replace(/\s/g, '');
        request.surnameCompanyName = dataForm.controls.accountHolderName.value;
        request.idBusinessRegistrationNumber = dataForm.controls.idNumber.value;
        request.cellNumber = dataForm.controls.accountHolderCellNumber.value;
        request.idType = isIdNumber ? IdType.Id : IdType.Passport;
        request.accountHolderPermission = dataForm.controls.accountHolderPermission.value;

        return request;
    }

    setBusinessRequestObject(
        dataForm: UntypedFormGroup,
        paymentDetails: PaymentDetails,
    ): PaymentDetails {
        const request: PaymentDetails = cloneDeep(paymentDetails);
        request.accountHolderType = paymentDetails.accountHolderType;
        request.accountHolderName = dataForm.controls.accountHolderName.value;
        request.accountNumber = dataForm.controls.accountNumber.value.replace(/\s/g, '');
        request.surnameCompanyName = dataForm.controls.businessRepresentativeName.value;
        request.idBusinessRegistrationNumber = dataForm.controls.businessRegistrationNumber.value;
        request.cellNumber = dataForm.controls.accountHolderCellNumber.value;
        request.idType = IdType.Passport;
        request.accountHolderPermission = dataForm.controls.accountHolderPermission.value;

        return request;
    }

    clearFields(
        accountHolderCode: string,
        dataForm: UntypedFormGroup,
        clearId: { showPassport: boolean; showId: boolean },
    ) {
        switch (accountHolderCode) {
            case AccountHolderTypes.POLICY_HOLDER:
                dataForm.controls.accountNumber.setValue('');
                break;
            case AccountHolderTypes.HOUSEHOLD_MEMBER:
                dataForm.controls.accountNumber.setValue('');
                dataForm.controls.accountHolderCellNumber.setValue('');
                dataForm.controls.accountHolderPermission.setValue(false);
                if (clearId.showPassport && dataForm?.controls?.idNumber) {
                    dataForm.controls.idNumber.setValue('');
                }
                break;
            case AccountHolderTypes.ANOTHER_INDIVIDUAL:
                dataForm.controls.accountHolderName.setValue('');
                dataForm.controls.accountNumber.setValue('');
                dataForm.controls.idNumber.setValue('');
                dataForm.controls.accountHolderCellNumber.setValue('');
                dataForm.controls.accountHolderPermission.setValue(false);
                break;
            case AccountHolderTypes.BUSINESS_OR_ORGANISATION:
                dataForm.controls.accountHolderName.setValue('');
                dataForm.controls.accountNumber.setValue('');
                dataForm.controls.businessRegistrationNumber.setValue('');
                dataForm.controls.businessRepresentativeName.setValue('');
                dataForm.controls.accountHolderCellNumber.setValue('');
                dataForm.controls.accountHolderPermission.setValue(false);
                break;
        }
    }

    addNameSurnameValidator(form: Signal<AccountDetailsPageData>, dataForm: UntypedFormGroup) {
        dataForm.controls[form().txtAccountHolderName.details.formControlName]?.addValidators(
            nameSurnameValidator(
                dataForm.controls[form().txtAccountHolderName.details.formControlName],
            ),
        );
    }

    addDynamicControls(
        dataForm: UntypedFormGroup,
        formBuilder: UntypedFormBuilder,
        fields: (FormInput | FormText)[],
    ) {
        addFormControls(dataForm, formBuilder, fields);
    }

    changeDataForm(
        accountHolderCode: string,
        form: AccountDetailsPageData,
        formBuilder: UntypedFormBuilder,
        dataForm: UntypedFormGroup,
        avsLimitReached: Signal<boolean>,
    ) {
        const showPassportOrId = this.showPassportOrId();

        switch (accountHolderCode) {
            case AccountHolderTypes.POLICY_HOLDER:
                this.changePolicyHolderDataForm(dataForm, form);
                break;
            case AccountHolderTypes.HOUSEHOLD_MEMBER:
                this.changeHouseholdMemberDataForm(dataForm, formBuilder, form, showPassportOrId);
                break;
            case AccountHolderTypes.ADDITIONAL_HOUSEHOLD_MEMBER:
                this.changeAdditionalHouseholdMemberDataForm(
                    dataForm,
                    formBuilder,
                    form,
                    showPassportOrId,
                );
                break;
            case AccountHolderTypes.ANOTHER_INDIVIDUAL:
                this.changeAnotherIndividualDataForm(dataForm, formBuilder, form);
                break;
            case AccountHolderTypes.BUSINESS_OR_ORGANISATION:
                this.changeBusinessOrOrgDataForm(dataForm, formBuilder, form);
                break;
        }

        this.disableFields(avsLimitReached, accountHolderCode, dataForm);
        this.clearFields(accountHolderCode, dataForm, showPassportOrId);
    }

    private changeBusinessOrOrgDataForm(
        dataForm: FormGroup,
        formBuilder: UntypedFormBuilder,
        form: AccountDetailsPageData,
    ) {
        this.addDynamicControls(dataForm, formBuilder, [
            form.txtAccountHolderName.details,
            form.txtAccountHolderCellNumber.details,
            form.txtBusinessRepresentativeName.details,
            form.txtBusinessRegistrationNumber.details,
            form.ckbPermission.details,
        ]);
        removeFormControls(dataForm, [form.txtAccountHolderIDNumber.details.formControlName]);
    }

    private changeAnotherIndividualDataForm(
        dataForm: FormGroup,
        formBuilder: UntypedFormBuilder,
        form: AccountDetailsPageData,
    ) {
        this.addDynamicControls(dataForm, formBuilder, [
            form.txtAccountHolderName.details,
            form.txtAccountHolderIDNumber.details,
            form.txtAccountHolderCellNumber.details,
            form.ckbPermission.details,
        ]);
        removeFormControls(dataForm, [
            form.txtBusinessRepresentativeName.details.formControlName,
            form.txtBusinessRegistrationNumber.details.formControlName,
        ]);
    }

    private changeAdditionalHouseholdMemberDataForm(
        dataForm: FormGroup,
        formBuilder: UntypedFormBuilder,
        form: AccountDetailsPageData,
        showPassportOrId: PassportOrId,
    ) {
        this.addDynamicControls(dataForm, formBuilder, [
            form.txtAccountHolderCellNumber.details,
            showPassportOrId.showPassport ? form.txtAccountHolderPassportNumber.details : null,
            showPassportOrId.showId ? form.txtAccountHolderIDNumber.details : null,
            form.ckbPermission.details,
        ]);
        removeFormControls(dataForm, [
            form.txtAccountHolderName.details.formControlName,
            !showPassportOrId.showPassport
                ? form.txtAccountHolderPassportNumber.details.formControlName
                : '',
            !showPassportOrId.showId ? form.txtAccountHolderIDNumber.details.formControlName : '',
            form.txtBusinessRepresentativeName.details.formControlName,
            form.txtBusinessRegistrationNumber.details.formControlName,
        ]);
    }

    private changeHouseholdMemberDataForm(
        dataForm: FormGroup,
        formBuilder: UntypedFormBuilder,
        form: AccountDetailsPageData,
        showPassportOrId: PassportOrId,
    ) {
        this.addDynamicControls(dataForm, formBuilder, [
            form.txtAccountHolderCellNumber.details,
            showPassportOrId.showPassport ? form.txtAccountHolderPassportNumber.details : null,
            showPassportOrId.showId ? form.txtAccountHolderIDNumber.details : null,
            form.ckbPermission.details,
        ]);
        removeFormControls(dataForm, [
            form.txtAccountHolderName.details.formControlName,
            !showPassportOrId.showPassport
                ? form.txtAccountHolderIDNumber.details.formControlName
                : '',
            !showPassportOrId.showId ? form.txtAccountHolderIDNumber.details.formControlName : '',
            form.txtBusinessRepresentativeName.details.formControlName,
            form.txtBusinessRegistrationNumber.details.formControlName,
        ]);
    }

    private changePolicyHolderDataForm(dataForm: FormGroup, form: AccountDetailsPageData) {
        removeFormControls(dataForm, [
            form.txtAccountHolderName.details.formControlName,
            form.txtAccountHolderIDNumber.details.formControlName,
            form.txtAccountHolderCellNumber.details.formControlName,
            form.ckbPermission.details.formControlName,
            form.txtBusinessRepresentativeName.details.formControlName,
            form.txtBusinessRegistrationNumber.details.formControlName,
        ]);
    }

    showDialog(
        form: Signal<AccountDetailsPageData>,
        response: PaymentDetails,
        avsLimitReached: Signal<boolean>,
        accountHolderCode: string,
        policyHolder: Signal<{ fullName: string; person: Person }>,
        houseHoldMember: Signal<{ fullName: string; person: Person }>,
    ) {
        const navRoute = form()?.btnNext?.details?.route;

        const avsDetails = response?.avsDetails;
        if (!avsDetails) return;

        if (avsDetails.servicesUnavailable) {
            return this.showAvsServiceUnavailableDialog(form, accountHolderCode);
        } else if (!avsDetails.success && avsLimitReached()) {
            return this.showAvsFinalFailureDialog(form, accountHolderCode);
        } else if (avsDetails.errors?.length > 0) {
            return this.showAvsFailureDialog(
                form,
                response,
                accountHolderCode,
                policyHolder().person,
                houseHoldMember().person,
            );
        }

        if (avsDetails.success) {
            if (accountHolderCode === AccountHolderTypes.POLICY_HOLDER) {
                this.quoteController?.navigate(navRoute);
            } else {
                this.showAvsThirdPartyConsentDialog(form);
            }
        }
    }

    showAvsFailureDialog(
        form: Signal<AccountDetailsPageData>,
        paymentDetails: PaymentDetails,
        accountHolderCode: string,
        policyHolder: Person,
        houseHoldMember: Person,
    ) {
        let id;
        switch (accountHolderCode) {
            case AccountHolderTypes.POLICY_HOLDER:
                id = policyHolder.identification.idNumber;
                break;
            case AccountHolderTypes.HOUSEHOLD_MEMBER:
                id = houseHoldMember.identification.idNumber;
                break;
            case AccountHolderTypes.ADDITIONAL_HOUSEHOLD_MEMBER:
            case AccountHolderTypes.ANOTHER_INDIVIDUAL:
            case AccountHolderTypes.BUSINESS_OR_ORGANISATION:
                id = paymentDetails.idBusinessRegistrationNumber;
                break;
        }

        this.dialogModal = this.modal.create({
            nzTitle: form().accountVerificationDialog?.txtTitle.details.html,
            nzContent: DialogComponent,
            nzData: {
                innerHtml: form().accountVerificationDialog?.txtContent.details.html,
                avsFailureReasons: paymentDetails.avsDetails?.errors,
                id: id,
                confirmBtnData: {
                    id: form().accountVerificationDialog?.btnRetry.id,
                    label: form().accountVerificationDialog?.btnRetry.details.label,
                },
            },
            nzFooter: null,
            nzCentered: true,
            nzClosable: false,
            nzMaskClosable: false,
            nzKeyboard: false,
        });

        const instance = this.dialogModal.getContentComponent();
        instance.confirm.pipe(untilDestroyed(this)).subscribe(() => {
            this.dialogModal.close();
        });
        instance.cancel.pipe(untilDestroyed(this)).subscribe(() => {
            this.dialogModal.close();
        });
    }

    showAvsFinalFailureDialog(form: Signal<AccountDetailsPageData>, accountHolderCode: string) {
        this.dialogModal = this.modal.create({
            nzTitle: form().avsFinalFailureDialog?.txtTitle.details.html,
            nzContent: DialogComponent,
            nzData: {
                innerHtml: form().avsFinalFailureDialog?.txtContent.details.html,
                confirmBtnData: {
                    id: form().avsFinalFailureDialog?.btnCallMeBack.id,
                    label: form().avsFinalFailureDialog?.btnCallMeBack.details.label,
                },
                cancelBtnData: {
                    id: form().avsFinalFailureDialog?.btnDropOff.id,
                    label: form().avsFinalFailureDialog?.btnDropOff.details.label,
                },
            },
            nzFooter: null,
            nzCentered: true,
            nzClosable: false,
            nzMaskClosable: false,
            nzKeyboard: false,
        });

        const instance = this.dialogModal.getContentComponent();
        instance.confirm.pipe(untilDestroyed(this)).subscribe(() => {
            this.quoteController.goToDropOff(
                form()?.avsFinalFailureDialog.btnCallMeBack.details.route.pageRoute,
            );
            this.dialogModal.close();
        });
        instance.cancel.pipe(untilDestroyed(this)).subscribe(() => {
            this.quoteController.goToDropOff(
                form()?.avsFinalFailureDialog.btnDropOff.details.route.pageRoute,
                null,
                DropOffCode.UNACCEPTABLE_RISK,
            );
            this.dialogModal.close();
        });
    }

    showAvsServiceUnavailableDialog(
        form: Signal<AccountDetailsPageData>,
        accountHolderCode: string,
    ) {
        this.dialogModal = this.modal.create({
            nzTitle: form().avsServiceUnavailableDialog?.txtTitle.details.html,
            nzContent: DialogComponent,
            nzData: {
                innerHtml: form().avsServiceUnavailableDialog?.txtContent.details.html,
                confirmBtnData: {
                    id: form().avsServiceUnavailableDialog?.btnGotIt.id,
                    label: form().avsServiceUnavailableDialog?.btnGotIt.details.label,
                },
            },
            nzFooter: null,
            nzCentered: true,
            nzClosable: false,
            nzMaskClosable: false,
            nzKeyboard: false,
        });

        const instance = this.dialogModal.getContentComponent();
        instance.confirm.pipe(untilDestroyed(this)).subscribe(() => {
            const navRoute = form()?.btnNext.details.route;
            this.dialogModal.close();
            if (accountHolderCode == AccountHolderTypes.POLICY_HOLDER) {
                this.quoteController.navigate(navRoute);
            } else {
                this.showAvsThirdPartyConsentDialog(form);
            }
        });
        instance.cancel.pipe(untilDestroyed(this)).subscribe(() => {
            this.dialogModal.close();
        });
    }

    showAvsThirdPartyConsentDialog(form: Signal<AccountDetailsPageData>) {
        this.dialogModal = this.modal.create({
            nzTitle: form().avsThirdPartyConsentDialog?.txtTitle.details.html,
            nzContent: DialogComponent,
            nzData: {
                innerHtml: form().avsThirdPartyConsentDialog?.txtContent.details.html,
                confirmBtnData: {
                    id: form().avsThirdPartyConsentDialog?.btnGotIt.id,
                    label: form().avsThirdPartyConsentDialog?.btnGotIt.details.label,
                },
            },
            nzFooter: null,
            nzCentered: true,
            nzClosable: false,
            nzMaskClosable: false,
            nzKeyboard: false,
        });

        const instance = this.dialogModal.getContentComponent();
        instance.confirm.pipe(untilDestroyed(this)).subscribe(() => {
            const navRoute = form()?.btnNext.details.route;
            this.dialogModal.close();

            this.quoteController.navigate(navRoute);
        });
        instance.cancel.pipe(untilDestroyed(this)).subscribe(() => {
            this.dialogModal.close();
        });
    }
}
